<script>import { getElementFont } from "../../util/text-size.js";
import * as d3 from "d3";
import { onMount } from "svelte";
import { TimelineScale } from "./timeline-scale.js";
export let role = "timeline";
export let present = /* @__PURE__ */ new Date();
export let scale;
export let start = void 0;
export let end = void 0;
export let ether;
export let timeZone = "UTC";
export let locale;
export let boundaryStartSymbol = "\u21E4";
export let boundaryEndSymbol = "\u21E5";
export let tickTextLeftMargin = 3;
export let tickTextRightMargin = 20;
export let minorTickTextVerticalAlign = 0.3;
export let majorTickTextVerticalAlign = 0.3;
export let boundsTextVerticalAlign = 1;
export let definition = void 0;
let startMarkerTime = start !== void 0 ? start : scale.domain()[0];
let endMarkerTime = end !== void 0 ? end : scale.domain()[1];
let startMarkerPosition = scale(startMarkerTime);
let endMarkerPosition = scale(endMarkerTime);
let axisContainerNode;
let minorAxisNode;
let majorAxisNode;
let minorBGNode;
let pastBGNode;
let presentBGNode;
let futureBGNode;
let boundsStartNode;
let boundsEndNode;
let minorFontProps;
let majorFontProps;
let boundsFontProps;
let minorHeight;
let majorHeight;
let boundsHeight;
let minorTickTextTopMargin;
let majorTickTextTopMargin;
let boundsTickTextTopMargin;
let tls;
let mounted = false;
onMount(() => {
  minorAxisNode = axisContainerNode.querySelector("g.sub-axis.minor");
  majorAxisNode = axisContainerNode.querySelector("g.sub-axis.major");
  boundsStartNode = axisContainerNode.querySelector("text.bounds.start");
  boundsEndNode = axisContainerNode.querySelector("text.bounds.end");
  minorBGNode = axisContainerNode.querySelector("rect.minorbg");
  pastBGNode = axisContainerNode.querySelector("rect.pastbg");
  presentBGNode = axisContainerNode.querySelector("rect.presentbg");
  futureBGNode = axisContainerNode.querySelector("rect.futurebg");
  minorFontProps = getElementFont(minorAxisNode);
  majorFontProps = getElementFont(majorAxisNode);
  boundsFontProps = getElementFont(boundsStartNode);
  minorHeight = parseFloat(minorFontProps.lineHeight);
  majorHeight = parseFloat(majorFontProps.lineHeight);
  boundsHeight = parseFloat(boundsFontProps.lineHeight);
  minorTickTextTopMargin = (minorHeight - parseInt(minorFontProps.size)) * minorTickTextVerticalAlign;
  majorTickTextTopMargin = (majorHeight - parseInt(majorFontProps.size)) * majorTickTextVerticalAlign;
  boundsTickTextTopMargin = (boundsHeight - parseInt(boundsFontProps.size)) * boundsTextVerticalAlign;
  mounted = true;
});
$: {
  if (mounted && tls) {
    const rangeInterval = tls.getRangeInterval();
    if (rangeInterval) {
      const nowIntervalStart = rangeInterval?.floor(present);
      const nowIntervalEnd = rangeInterval?.ceil(present);
      const nowRangeStart = scale(nowIntervalStart);
      const nowRangeEnd = scale(nowIntervalEnd);
      if (nowRangeStart > tls.getRange()[0]) {
        const pastStart = tls.getRange()[0];
        const pastEnd = Math.min(tls.getRange()[1], nowRangeStart);
        d3.select(pastBGNode).attr("x", pastStart).attr("width", pastEnd - pastStart).attr("y", minorHeight).attr("height", 2 * majorHeight);
      } else {
        d3.select(pastBGNode).attr("width", 0).attr("height", 0);
      }
      if (nowRangeEnd < tls.getRange()[1]) {
        const futureEnd = tls.getRange()[1];
        const futureStart = Math.max(tls.getRange()[0], nowRangeEnd);
        d3.select(futureBGNode).attr("x", futureStart).attr("width", futureEnd - futureStart).attr("y", minorHeight).attr("height", 2 * majorHeight);
      } else {
        d3.select(futureBGNode).attr("width", 0).attr("height", 0);
      }
      if (nowRangeStart < tls.getRange()[1] && nowRangeEnd > tls.getRange()[0]) {
        const presentStart = Math.max(tls.getRange()[0], nowRangeStart);
        const presentEnd = Math.min(tls.getRange()[1], nowRangeEnd);
        d3.select(presentBGNode).attr("x", presentStart).attr("width", presentEnd - presentStart).attr("y", minorHeight).attr("height", 2 * majorHeight);
      } else {
        d3.select(presentBGNode).attr("width", 0).attr("height", 0);
      }
    }
  }
}
$: {
  if (mounted) {
    tls = new TimelineScale(
      scale.domain(),
      scale.range(),
      timeZone,
      locale ?? navigator.language,
      tickTextLeftMargin,
      tickTextRightMargin,
      minorAxisNode,
      majorAxisNode
    );
    tls.scale(scale);
    definition = tls.getDefinition();
    const rangeInterval = tls.getRangeInterval();
    if (definition && rangeInterval) {
      d3.select(minorBGNode).attr("x", tls.getRange()[0]).attr("width", tls.getRange()[1] - tls.getRange()[0]).attr("y", 0).attr("height", minorHeight);
      let minorAxisGenerator = d3.axisBottom(scale).ticks(tls.getRangeInterval()).tickFormat((d) => definition.formatters.minor(d));
      if (role === "timeline") {
        minorAxisGenerator = minorAxisGenerator.tickSize(minorHeight);
      }
      const minorAxis = d3.select(minorAxisNode).call(minorAxisGenerator);
      minorAxis.selectAll("g.tick text").attr("text-anchor", "start").attr("y", 1 + minorTickTextTopMargin + "").attr("dy", "0.71em").attr("x", "0px").attr("dx", tickTextLeftMargin + "px");
      const domain = minorAxis.select(".domain");
      domain.attr("fill", "none");
      if (role === "timeline") {
        domain.attr("d", `M${tls.getRange()[0]},0H${tls.getRange()[1]}`);
      } else {
        domain.attr(
          "d",
          `M ${tls.getRange()[0]} ${minorTickTextTopMargin} V 0 H ${tls.getRange()[1]} V ${minorTickTextTopMargin}`
        );
      }
      const majorInterval = tls.getMajorRangeInterval();
      if (majorInterval) {
        let majorAxisGenerator = d3.axisBottom(scale).ticks(tls.getMajorRangeInterval()).tickFormat((d) => definition.formatters.major(d));
        majorAxisGenerator = majorAxisGenerator.tickSize(minorHeight + majorHeight);
        let majorAxis = d3.select(majorAxisNode).call(majorAxisGenerator);
        majorAxis.selectAll("g.tick text").attr("text-anchor", "start").attr("y", 1 + majorTickTextTopMargin + minorHeight + "").attr("dy", "0.71em").attr("x", "0px").attr("dx", tickTextLeftMargin + "px");
        majorAxis.select(".domain").remove();
      }
      startMarkerTime = start !== void 0 ? start : scale.domain()[0];
      endMarkerTime = end !== void 0 ? end : scale.domain()[1];
      startMarkerPosition = scale(startMarkerTime);
      endMarkerPosition = scale(endMarkerTime);
      d3.select(boundsStartNode).classed("bounds start", true).attr("text-anchor", "start").attr("fill", "currentColor").attr("y", 1 + minorHeight + majorHeight + boundsTickTextTopMargin + "").attr("dy", "0.71em").attr("x", startMarkerPosition).text(boundaryStartSymbol + " " + definition.formatters.bound(new Date(startMarkerTime)));
      d3.select(boundsEndNode).classed("bounds end", true).attr("text-anchor", "end").attr("fill", "currentColor").attr("y", 1 + minorHeight + majorHeight + boundsTickTextTopMargin + "").attr("dy", "0.71em").attr("x", endMarkerPosition).text(definition.formatters.bound(new Date(endMarkerTime)) + " " + boundaryEndSymbol);
      updateEtherLines(ether);
    }
  }
}
const updateEtherLines = (ether2) => {
  const useEtherLines = ether2 !== void 0 && ether2 < 0;
  minorAxisNode.querySelectorAll(".tick").forEach((tick) => {
    const translate = tick.getAttribute("transform");
    const xPosition = parseFloat(translate.substring(10, translate.length - 3));
    const isVisible = xPosition >= startMarkerPosition && xPosition <= endMarkerPosition;
    if (tick.lastElementChild?.tagName.toLowerCase() === "line") {
      if (useEtherLines && isVisible) {
        tick.lastElementChild.setAttribute("y2", ether2.toString());
      } else {
        tick.removeChild(tick.lastElementChild);
      }
    } else {
      if (useEtherLines && isVisible) {
        const lineNode = document.createElementNS("http://www.w3.org/2000/svg", "line");
        lineNode.classList.add("ether");
        lineNode.setAttribute("stroke", "currentColor");
        lineNode.setAttribute("y2", ether2.toString());
        tick.appendChild(lineNode);
      } else {
      }
    }
  });
};
$: {
  if (mounted) {
    updateEtherLines(ether);
  }
}
</script>

<g class="axis time-axis {role}" bind:this={axisContainerNode}>
	<rect class="minorbg" />
	<rect class="pastbg" />
	<rect class="presentbg" />
	<rect class="futurebg" />
	<g class="sub-axis major" />
	<g class="sub-axis minor" />
	<text class="bounds start" text-anchor="start" fill="currentColor" dy="0.71em" />
	<text class="bounds end" text-anchor="end" fill="currentColor" dy="0.71em" />
</g>

<style>
	g.time-axis.plot > rect.minorbg,
	g.time-axis.plot > rect.pastbg,
	g.time-axis.plot > rect.presentbg,
	g.time-axis.plot > rect.futurebg {
		display: none;
	}

	g.time-axis > rect.minorbg {
		fill: #1a1b1f;
		fill-opacity: 0.8;
	}
	g.time-axis > rect.pastbg {
		fill: #5c3483;
	}
	g.time-axis > rect.presentbg {
		fill: #348334;
	}
	g.time-axis > rect.futurebg {
		fill: #7b65b3;
	}

	g.time-axis > g.sub-axis.minor {
		font-size: 14px;
		line-height: 22px;
	}
	g.time-axis > g.sub-axis.major {
		font-weight: bold;
		font-size: 14px;
		line-height: 21px;
	}
	g.time-axis > text.bounds {
		font-weight: bold;
		font-size: 12px;
		line-height: 21px;
	}
</style>
